import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Popconfirm,
  Typography,
  Statistic,
  Row,
  Col,
  Progress,
  Tooltip,
  Drawer,
  InputNumber,
  Select
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface Budget {
  id: string;
  name: string;
  description: string | null;
  periodStart: string;
  periodEnd: string;
  state: string;
  version: number;
  createdAt: string;
}

interface BudgetVariance {
  budgetId: string;
  budgetName: string;
  periodStart: string;
  periodEnd: string;
  lines: {
    accountId: string;
    accountCode: string;
    accountName: string;
    accountType: string;
    plannedAmount: number;
    actualAmount: number;
    variance: number;
    variancePercent: number;
    status: 'OVER' | 'UNDER' | 'ON_TRACK';
  }[];
  totals: {
    plannedAmount: number;
    actualAmount: number;
    variance: number;
    variancePercent: number;
  };
}

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

const BudgetManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [varianceDrawerVisible, setVarianceDrawerVisible] = useState(false);
  const [selectedBudgetVariance, setSelectedBudgetVariance] = useState<BudgetVariance | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchBudgets();
    fetchAccounts();
  }, []);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/accounting/budgets');
      setBudgets(response.data || []);
    } catch (error: any) {
      message.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await apiService.get('/accounting/accounts');
      setAccounts(response.data || []);
    } catch (error: any) {
      console.error('Failed to load accounts:', error);
    }
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      ACTIVE: 'green',
      CLOSED: 'red'
    };
    return colors[state] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      ON_TRACK: 'green',
      UNDER: 'blue',
      OVER: 'red'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleCreate = () => {
    setEditingBudget(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Budget) => {
    setEditingBudget(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      dateRange: [dayjs(record.periodStart), dayjs(record.periodEnd)]
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [periodStart, periodEnd] = values.dateRange;

      const data = {
        name: values.name,
        description: values.description,
        periodStart: periodStart.format('YYYY-MM-DD'),
        periodEnd: periodEnd.format('YYYY-MM-DD')
      };

      if (editingBudget) {
        await apiService.put(`/accounting/budgets/${editingBudget.id}`, data);
        message.success('Budget updated');
      } else {
        await apiService.post('/accounting/budgets', data);
        message.success('Budget created');
      }

      setIsModalVisible(false);
      fetchBudgets();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await apiService.post(`/accounting/budgets/${id}/activate`);
      message.success('Budget activated');
      fetchBudgets();
    } catch (error: any) {
      message.error(error.message || 'Failed to activate budget');
    }
  };

  const handleClose = async (id: string) => {
    try {
      await apiService.post(`/accounting/budgets/${id}/close`);
      message.success('Budget closed');
      fetchBudgets();
    } catch (error: any) {
      message.error(error.message || 'Failed to close budget');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`/accounting/budgets/${id}`);
      message.success('Budget deleted');
      fetchBudgets();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete budget');
    }
  };

  const handleViewVariance = async (budget: Budget) => {
    try {
      const response = await apiService.get(`/accounting/budgets/${budget.id}/variance`);
      setSelectedBudgetVariance(response.data);
      setVarianceDrawerVisible(true);
    } catch (error: any) {
      message.error('Failed to load variance report');
    }
  };

  const columns: ColumnsType<Budget> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (
        <Text>
          {dayjs(record.periodStart).format('MMM YYYY')} - {dayjs(record.periodEnd).format('MMM YYYY')}
        </Text>
      )
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (v: number) => <Tag>v{v}</Tag>
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => (
        <Tag color={getStateColor(state)}>{state}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Variance">
            <Button
              type="text"
              icon={<BarChartOutlined />}
              onClick={() => handleViewVariance(record)}
            />
          </Tooltip>
          {record.state === 'DRAFT' && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Tooltip title="Activate">
                <Popconfirm
                  title="Activate this budget?"
                  onConfirm={() => handleActivate(record.id)}
                >
                  <Button type="text" icon={<CheckCircleOutlined />} />
                </Popconfirm>
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this budget?"
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            </>
          )}
          {record.state === 'ACTIVE' && (
            <Tooltip title="Close">
              <Popconfirm
                title="Close this budget?"
                onConfirm={() => handleClose(record.id)}
              >
                <Button type="text" icon={<CloseCircleOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const varianceColumns: ColumnsType<BudgetVariance['lines'][0]> = [
    { title: 'Code', dataIndex: 'accountCode', key: 'accountCode', width: 100 },
    { title: 'Account', dataIndex: 'accountName', key: 'accountName' },
    { title: 'Type', dataIndex: 'accountType', key: 'accountType', width: 100 },
    {
      title: 'Planned',
      dataIndex: 'plannedAmount',
      key: 'plannedAmount',
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Actual',
      dataIndex: 'actualAmount',
      key: 'actualAmount',
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      align: 'right',
      render: (val: number) => (
        <Text style={{ color: val >= 0 ? '#52c41a' : '#f5222d' }}>
          {formatCurrency(val)}
        </Text>
      )
    },
    {
      title: 'Variance %',
      dataIndex: 'variancePercent',
      key: 'variancePercent',
      align: 'right',
      render: (val: number) => `${val.toFixed(1)}%`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.replace('_', ' ')}</Tag>
      )
    }
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <DollarOutlined />
            <span>Budget Management</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Budget
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={budgets}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingBudget ? 'Edit Budget' : 'New Budget'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Budget Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., Q1 2025 Operating Budget" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            name="dateRange"
            label="Budget Period"
            rules={[{ required: true, message: 'Please select period' }]}
          >
            <RangePicker style={{ width: '100%' }} picker="month" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Variance Report Drawer */}
      <Drawer
        title={`Budget Variance Report: ${selectedBudgetVariance?.budgetName || ''}`}
        placement="right"
        width={900}
        open={varianceDrawerVisible}
        onClose={() => setVarianceDrawerVisible(false)}
      >
        {selectedBudgetVariance && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Planned"
                    value={selectedBudgetVariance.totals.plannedAmount}
                    formatter={(v) => formatCurrency(Number(v))}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Actual"
                    value={selectedBudgetVariance.totals.actualAmount}
                    formatter={(v) => formatCurrency(Number(v))}
                    valueStyle={{ color: selectedBudgetVariance.totals.actualAmount <= selectedBudgetVariance.totals.plannedAmount ? '#52c41a' : '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Variance"
                    value={selectedBudgetVariance.totals.variance}
                    formatter={(v) => formatCurrency(Number(v))}
                    valueStyle={{ color: Number(selectedBudgetVariance.totals.variance) >= 0 ? '#52c41a' : '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Variance %"
                    value={selectedBudgetVariance.totals.variancePercent}
                    precision={1}
                    suffix="%"
                    valueStyle={{ color: selectedBudgetVariance.totals.variancePercent >= 0 ? '#52c41a' : '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="Budget Lines">
              <Table
                columns={varianceColumns}
                dataSource={selectedBudgetVariance.lines}
                rowKey="accountId"
                pagination={false}
                size="small"
              />
            </Card>
          </>
        )}
      </Drawer>
    </>
  );
};

export default BudgetManagement;
